import React from 'react';
import {useIntl} from 'react-intl';
import {toast} from 'react-toastify';
import {RoutePath} from 'constants/app.constants';
import {useAddWorkshopUsers, useCreateWorkshop, useDeleteWorkshop, useDuplicateWorkshop, useExportAsRecipe, useSaveWorkshop} from 'services/query/modules';
import {useLocalizedNavigation} from './useLocalizedNavigation';
import {queryClient} from 'services';

type UseWorkshopActionsProps = {
  workshopId?: number;
  isRecipe?: boolean;
  locale?: ToolboxObject.Locale;
};

export const useWorkshopActions = (props: UseWorkshopActionsProps = {}) => {
  const navigate = useLocalizedNavigation();
  const intl = useIntl();
  const {workshopId, isRecipe} = props;

  const {mutate: deleteWorkshop, isLoading: isDeleteLoading} = useDeleteWorkshop(isRecipe);
  const handleDelete = React.useCallback(
    (newOwnerId?: number) => {
      if (!workshopId) return;

      deleteWorkshop(
        {id: workshopId, newOwnerId},
        {
          onSuccess: async () => {
            toast.success(`${isRecipe ? 'Recipe' : 'Workshop'} deleted successfully`);
            queryClient.removeQueries([isRecipe ? 'recipes' : 'workshops', workshopId]);

            navigate(isRecipe ? RoutePath.library : RoutePath.workshopLibrary);
            await queryClient.invalidateQueries([isRecipe ? 'recipes' : 'workshops']);
          },
          onError: () => {
            return toast.error(`Something went wrong, ${isRecipe ? 'Recipe' : 'Workshop'} could not be deleted`);
          },
        }
      );
    },
    [workshopId, deleteWorkshop, navigate]
  );

  const {mutate: createRecipe, isLoading: isCreateRecipeLoading} = useExportAsRecipe(Number(workshopId));
  const handleExportAsRecipe = React.useCallback(
    (data: {category: ToolboxObject.WorkshopCategory | null; teaser: string | null}) => {
      if (!workshopId) return;

      createRecipe(data, {
        onSuccess: () => {
          navigate('/library');
          return toast.success('Exported workshop as recipe successfully');
        },
        onError: () => toast.error(`Something went wrong, recipe could not be saved`),
      });
    },
    [createRecipe]
  );

  const {mutate: addWorkshopUsers, isLoading: isAddWorkshopUserLoading} = useAddWorkshopUsers(Number(workshopId));
  const handleAddWorkshopUsers = (data: {userIds: number[]; access: ToolboxObject.WorkshopUserAccess}) => {
    if (!workshopId) return;

    addWorkshopUsers(data, {
      onSuccess: () => toast.success(intl.formatMessage({id: 'toast.workshop.share.success'})),
      onError: () => toast.error(intl.formatMessage({id: 'toast.workshop.share.fail'})),
    });
  };

  const {mutate: duplicateWorkshop, isLoading: isDuplicateLoading} = useDuplicateWorkshop(Number(workshopId));
  const handleDuplicate = (title: string) => {
    if (!workshopId) return;

    duplicateWorkshop(
      {title},
      {
        onSuccess: (savedWorkshop) => {
          toast.success(intl.formatMessage({id: 'toast.workshop.duplicate.success'}));
          return navigate(`/editor/${savedWorkshop.id}`);
        },
        onError: () => toast.error(intl.formatMessage({id: 'toast.workshop.duplicate.fail'})),
      }
    );
  };

  return {
    handleDelete,
    handleExportAsRecipe,
    handleAddWorkshopUsers,
    handleDuplicate,
    isLoading: isCreateRecipeLoading || isDeleteLoading || isAddWorkshopUserLoading || isDuplicateLoading,
  };
};

export const useSaveWorkshopActions = (props: UseWorkshopActionsProps = {}) => {
  const navigate = useLocalizedNavigation();
  const intl = useIntl();
  const {workshopId, isRecipe, locale = 'en'} = props;

  const {mutate: save, isLoading: isSaveLoading, data, isSuccess} = useSaveWorkshop(isRecipe);
  const handleSave = React.useCallback(
    (data: ToolboxObject.WorkshopCreateData) => {
      if (!workshopId) return;

      const saveData = {
        id: workshopId,
        ...data,
        category: isRecipe ? data.category : null,
        teaser: isRecipe ? data.teaser : null,
        language: locale,
      };
      save(saveData, {
        onSuccess: () => toast.success(intl.formatMessage({id: isRecipe ? 'toast.recipe.update.success' : 'toast.workshop.update.success'})),
        onError: () => toast.error(intl.formatMessage({id: isRecipe ? 'toast.recipe.update.fail' : 'toast.workshop.update.fail'})),
      });
    },
    [workshopId, save, locale, isRecipe]
  );

  const {mutate: create, isLoading: isCreateLoading} = useCreateWorkshop(isRecipe);
  const handleCreate = React.useCallback(
    (data: ToolboxObject.WorkshopCreateData) => {
      create(
        {...data, language: locale},
        {
          onSuccess: (savedWorkshop) => {
            toast.success(intl.formatMessage({id: 'toast.workshop.create.success'}));
            return navigate(`/editor/${savedWorkshop.id}`);
          },
          onError: () => toast.error(intl.formatMessage({id: 'toast.workshop.create.fail'})),
        }
      );
    },
    [create, navigate, locale, isRecipe]
  );

  return {
    handleCreate,
    handleSave,
    savedWorkshop: data,
    isSuccess,
    isLoading: isCreateLoading || isSaveLoading,
  };
};
